import { useEffect, useState, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if device is blocked and force logout
  const checkDeviceBlocked = async (userId: string) => {
    try {
      const fingerprint = generateFingerprint();
      const { data, error } = await supabase
        .from("user_devices")
        .select("is_blocked")
        .eq("user_id", userId)
        .eq("device_fingerprint", fingerprint)
        .maybeSingle();

      if (!error && data?.is_blocked) {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        window.location.href = "/auth?banned=true";
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const LAST_LOGIN_KEY = "last_login_at";
    const THREE_MONTHS_MS = 90 * 24 * 60 * 60 * 1000;

    // Force logout if last login was more than 3 months ago
    const enforceMaxSessionAge = async (): Promise<boolean> => {
      const raw = localStorage.getItem(LAST_LOGIN_KEY);
      if (!raw) return false;
      const last = parseInt(raw, 10);
      if (isNaN(last)) return false;
      if (Date.now() - last > THREE_MONTHS_MS) {
        localStorage.removeItem(LAST_LOGIN_KEY);
        await supabase.auth.signOut();
        return true;
      }
      return false;
    };

    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Check if user is banned
      if (session?.user) {
        const bannedUntil = session.user.banned_until;
        if (bannedUntil && new Date(bannedUntil) > new Date()) {
          setTimeout(async () => {
            await supabase.auth.signOut();
            setSession(null);
            setUser(null);
            setLoading(false);
            window.location.href = "/auth?banned=true";
          }, 0);
          return;
        }
      }

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (event === "SIGNED_IN" && session?.user) {
        // Record login time — session stays valid for 3 months from this moment
        localStorage.setItem(LAST_LOGIN_KEY, Date.now().toString());

        // Clear security flags on every new sign-in
        localStorage.removeItem("biometric_authenticated");
        localStorage.removeItem("passkey_prompted");

        // Mark that MFA needs to be verified for this login
        const mfaData = localStorage.getItem("mfa_verified");
        const mfaValid = mfaData ? JSON.parse(mfaData) : null;
        const threeMonths = 90 * 24 * 60 * 60 * 1000;
        if (!mfaValid || mfaValid.userId !== session.user.id || Date.now() - mfaValid.timestamp > threeMonths) {
          localStorage.setItem("mfa_required", "true");
          localStorage.removeItem("mfa_verified");
        }
        setTimeout(() => {
          registerDevice(session.user.id);
          checkDeviceBlocked(session.user.id);
        }, 0);
      }
      if (event === "SIGNED_OUT") {
        localStorage.removeItem(LAST_LOGIN_KEY);
        localStorage.removeItem("mfa_verified");
        localStorage.removeItem("mfa_required");
        localStorage.removeItem("biometric_authenticated");
        localStorage.removeItem("passkey_prompted");
      }
    });

    // THEN check for existing session — enforce 3-month max age first
    (async () => {
      const expired = await enforceMaxSessionAge();
      if (expired) {
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      // Seed last_login_at for existing sessions that predate this feature
      if (session?.user && !localStorage.getItem(LAST_LOGIN_KEY)) {
        localStorage.setItem(LAST_LOGIN_KEY, Date.now().toString());
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        setTimeout(() => {
          registerDevice(session.user.id);
          checkDeviceBlocked(session.user.id);
        }, 0);
      }
    })();

    return () => subscription.unsubscribe();
  }, []);

  // Periodic device block check every 30 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      checkDeviceBlocked(user.id);
    }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    
    // Update profile with full name and register device after signup
    // ملاحظة: إذا كان تأكيد البريد مفعّل، فلن يتم تسجيل دخول المستخدم مباشرةً => لا يمكن إدخال الجهاز بسبب سياسات الأمان.
    if (!error && data.user) {
      const userId = data.user.id;

      // تحديث الاسم الكامل
      if (fullName) {
        setTimeout(async () => {
          await supabase
            .from("profiles")
            .update({ full_name: fullName })
            .eq("user_id", userId);
        }, 1000);
      }

      // تسجيل الجهاز فقط إذا كان هناك جلسة (أي تم تسجيل الدخول فعلياً بعد التسجيل)
      if (data.session?.user) {
        setTimeout(() => {
          registerDevice(userId);
        }, 0);
      }
    }
    
    return { error: error as Error | null };
  };

  const DEVICE_ID_STORAGE_KEY = "crebtopay_device_id";

  const getOrCreateDeviceId = (): string => {
    try {
      const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
      if (existing) return existing;

      const generated =
        typeof crypto !== "undefined" &&
        "randomUUID" in crypto &&
        typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

      localStorage.setItem(DEVICE_ID_STORAGE_KEY, generated);
      return generated;
    } catch {
      // إذا كان localStorage غير متاح (وضع خاص/صلاحيات)، نستخدم معرفاً مؤقتاً
      return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }
  };

  const generateFingerprint = (): string => {
    const deviceId = getOrCreateDeviceId();
    const nav = navigator;
    const screen = window.screen;
    const data = [
      deviceId,
      nav.userAgent,
      nav.language,
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      nav.hardwareConcurrency || "unknown",
    ].join("|");

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  };

  const getClientIP = async (): Promise<string> => {
    try {
      const { data, error } = await supabase.functions.invoke("get-client-ip");
      if (error) {
        console.error("Error getting IP:", error);
        return "unknown";
      }
      return data?.ip || "unknown";
    } catch (error) {
      console.error("Error getting IP:", error);
      return "unknown";
    }
  };

  const registerDevice = async (userId: string) => {
    try {
      const fingerprint = generateFingerprint();
      const userAgent = navigator.userAgent;
      const ipAddress = await getClientIP();

      // Check if device already exists
      const { data: existingDevice, error: lookupError } = await supabase
        .from("user_devices")
        .select("id")
        .eq("user_id", userId)
        .eq("device_fingerprint", fingerprint)
        .maybeSingle();

      if (lookupError) {
        console.error("Device lookup error:", lookupError);
      }

      if (existingDevice) {
        // Update last login and IP
        const { error: updateError } = await supabase
          .from("user_devices")
          .update({
            last_login: new Date().toISOString(),
            user_agent: userAgent,
            ip_address: ipAddress,
          })
          .eq("id", existingDevice.id);

        if (updateError) {
          console.error("Device update error:", updateError);
        }
      } else {
        // Insert new device with IP
        const { error: insertError } = await supabase.from("user_devices").insert({
          user_id: userId,
          device_fingerprint: fingerprint,
          user_agent: userAgent,
          ip_address: ipAddress,
          last_login: new Date().toISOString(),
        });

        if (insertError) {
          console.error("Device insert error:", insertError);
        }
      }
    } catch (error) {
      console.error("Error registering device:", error);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // تسجيل الجهاز سيتم عبر onAuthStateChange (SIGNED_IN)
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
